import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { ProductStatus } from '@data/utils/types';
import { fetchPmProducts } from '@data/api';
import * as queryKeys from '@data/constants/keys';
import { ProductHours, toProductHours } from './productTime';

// Hours per product per quarter, from the same windowed endpoint the trend uses, so the three cards reconcile.

// Archived and deactivated products are included: dropping them would not reconcile with the reporting screens.
const ALL_STATUSES = [ProductStatus.active, ProductStatus.support, ProductStatus.archived, ProductStatus.deactivated];

// Trailing quarters the portfolio is totalled over, the current (partial) one included.
export const QUARTER_COUNT = 5;

export interface Quarter {
  /** '2026-Q1' — the identity, and the query key. */
  key: string;
  /** 'Q1 2026' — how it is labelled. */
  label: string;
  from: Date;
  to: Date;
}

/** The `count` quarters ending with the one `now` falls in, oldest first. */
export const trailingQuarters = (now: Date, count: number): Quarter[] => {
  const currentQuarter = Math.floor(now.getMonth() / 3);

  return Array.from({ length: count }, (_, i) => {
    const offset = count - 1 - i;
    const year = now.getFullYear() + Math.floor((currentQuarter - offset) / 4);
    const quarter = ((((currentQuarter - offset) % 4) + 4) % 4) + 1;

    return {
      key: `${year}-Q${quarter}`,
      label: `Q${quarter} ${year}`,
      // Midnight UTC, not local: these go on the wire as instants, and local midnight is the evening before east of Greenwich.
      from: new Date(Date.UTC(year, (quarter - 1) * 3, 1)),
      // Day 0 of the next quarter's first month — the last day of this one, whatever its length.
      to: new Date(Date.UTC(year, quarter * 3, 0)),
    };
  });
};

/** Hours per product per window. One request each, which is how the endpoint is shaped. */
export const useProductHoursByWindow = (
  organizationId: string | undefined,
  windows: Quarter[],
): { byWindow: ProductHours[][]; isLoading: boolean; isError: boolean } => {
  const queries = useQueries({
    queries: windows.map(w => ({
      queryKey: [queryKeys.PM_PRODUCTS, { organizationId, quarter: w.key }],
      queryFn: () =>
        fetchPmProducts(organizationId!, {
          statuses: ALL_STATUSES,
          from: w.from.toISOString(),
          to: w.to.toISOString(),
        }),
      enabled: !!organizationId,
      staleTime: 60_000,
    })),
  });

  // The query objects are new on every render, so the memo keys on when the data last changed instead.
  const stamps = queries.map(q => q.dataUpdatedAt).join(',');
  const byWindow = useMemo(
    () => windows.map((_, i) => toProductHours(queries[i]?.data?.items)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [windows, stamps],
  );

  return { byWindow, isLoading: queries.some(q => q.isLoading), isError: queries.some(q => q.isError) };
};

/** Exact hours with thousands separators — "1,151.84" — for table figures. */
export const hoursExact = (hours: number): string =>
  new Intl.NumberFormat('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(hours);
